/**
 * VisibilityContext — Phase 4, Module 4 (AD-013).
 *
 * Loads the global visibility matrix once at app startup via `listenVisibilityMatrix`.
 * Provides `canSeeSection(section)` — a role-aware gate for the current user.
 *
 * Usage:
 *   const { canSeeSection, globalMatrix } = useVisibility();
 *   if (!canSeeSection('delivered_report')) return null;
 *
 * Role mapping: auth's `role` (Role from roles.ts) → PortalRole:
 *   admin  → 'admin'
 *   staff  → 'staff'
 *   client → 'primary_client' (best default for portal clients; participants
 *             may have a more specific role set per-project — that uses
 *             canSee() directly with the participant's role, not this hook)
 *   null/unknown → 'family' (least-permissive non-admin default)
 *
 * Per-project overrides: pass `projectOverrides` as a second param to `canSeeSection`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './auth';
import type { Role } from './roles';
import {
  APPROVED_DEFAULTS,
  canSee,
  type PortalRole,
  type PortalSection,
  type ProjectVisibilityOverrides,
  type VisibilityMatrix,
} from './visibilityModel';
import { listenVisibilityMatrix } from './visibilityStore';

// ─── Role bridge ──────────────────────────────────────────────────────────

/**
 * Map the AVBP login `Role` to a `PortalRole` for the global matrix gate.
 * Per-project participant roles are handled by calling `canSee()` directly
 * with the participant's stored role — not via this hook.
 */
export function loginRoleToPortalRole(role: Role | null | undefined): PortalRole {
  switch (role) {
    case 'admin':  return 'admin';
    case 'staff':  return 'staff';
    case 'client': return 'primary_client';
    default:       return 'family'; // most restrictive non-admin default
  }
}

// ─── Context ──────────────────────────────────────────────────────────────

interface VisibilityState {
  /** The live global visibility matrix (falls back to APPROVED_DEFAULTS). */
  globalMatrix: VisibilityMatrix;
  /**
   * Gate function for the current user's login role.
   * @param section        — section to check
   * @param projectOverrides — optional per-project overrides for this project
   */
  canSeeSection(section: PortalSection, projectOverrides?: ProjectVisibilityOverrides): boolean;
  /**
   * The current user's portal role (derived from login role).
   * For per-project participant roles, map the participant entry's role and
   * call `canSee()` directly.
   */
  portalRole: PortalRole;
}

const VisibilityCtx = createContext<VisibilityState | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────

export function VisibilityProvider({ children }: { children: ReactNode }) {
  const { role } = useAuth();
  const [globalMatrix, setGlobalMatrix] = useState<VisibilityMatrix>(APPROVED_DEFAULTS);

  // Subscribe to live global matrix.
  useEffect(() => {
    const unsub = listenVisibilityMatrix(setGlobalMatrix);
    return unsub;
  }, []);

  const portalRole = useMemo(() => loginRoleToPortalRole(role), [role]);

  const canSeeSection = useCallback(
    (section: PortalSection, projectOverrides?: ProjectVisibilityOverrides): boolean =>
      canSee(portalRole, section, globalMatrix, projectOverrides),
    [portalRole, globalMatrix],
  );

  const value = useMemo<VisibilityState>(
    () => ({ globalMatrix, canSeeSection, portalRole }),
    [globalMatrix, canSeeSection, portalRole],
  );

  return <VisibilityCtx.Provider value={value}>{children}</VisibilityCtx.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────

export function useVisibility(): VisibilityState {
  const ctx = useContext(VisibilityCtx);
  if (!ctx) throw new Error('useVisibility must be used inside <VisibilityProvider>.');
  return ctx;
}
